import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { Loader2, Rocket, CheckCircle2, AlertTriangle } from "lucide-react";

const RESULT_ROWS = [
  ["alunos_criados_com_cpf", "Alunos novos com CPF"], ["alunos_criados_sem_cpf", "Alunos novos sem CPF / só nome"],
  ["alunos_completados", "Cadastros existentes completados"], ["planos_criados", "Planos criados"],
  ["mensalidades_gravadas", "Mensalidades gravadas"], ["mensalidades_em_atraso", "Mensalidades em atraso"],
  ["mensalidades_a_vencer", "Mensalidades a vencer"], ["mensalidades_nao_ligadas", "Mensalidades sem aluno"], ["mensalidades_canceladas_ignoradas", "Canceladas no Next Fit (não entram)"],
  ["erros", "Erros"],
];

const ResultBox = ({ r }) => (
  <div className="space-y-3" data-testid="full-migration-result">
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
      {RESULT_ROWS.map(([k, label]) => (
        <div key={k} className="rounded-lg border border-slate-200 bg-white p-3">
          <div className="text-[11px] text-slate-500">{label}</div>
          <div className="text-xl font-black text-slate-900" data-testid={`full-result-${k}`}>{r[k] ?? 0}</div>
        </div>
      ))}
    </div>
    {r.conferir_total > 0 && (
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs" data-testid="full-result-conferir">
        <div className="font-bold text-amber-900 mb-1">{r.conferir_total} aluno(s) trazidos com aviso — confira</div>
        <div className="max-h-60 overflow-auto space-y-0.5">
          {(r.conferir || []).map((c, i) => <div key={i}><b>{c.aluno}</b> — {c.motivo}</div>)}
        </div>
      </div>
    )}
  </div>
);

export const NextFitFullMigration = ({ unitId, unitName, hasConfig }) => {
  const [job, setJob] = useState(null);
  const [starting, setStarting] = useState(false);
  const timer = useRef(null);
  const running = job && ["pending", "running"].includes(job.status);

  const load = async () => {
    try { const { data } = await api.get(`/integrations/nextfit/units/${unitId}/full-migrate`); setJob(data.job); } catch { /* sem job */ }
  };
  useEffect(() => { setJob(null); load(); }, [unitId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    clearInterval(timer.current);
    if (running) { timer.current = setInterval(load, 2000); }
    return () => { clearInterval(timer.current); };
  }, [running, unitId]); // eslint-disable-line react-hooks/exhaustive-deps

  const start = async () => {
    if (!window.confirm(`Migrar TODOS os alunos, contratos e mensalidades do Next Fit para ${unitName}? Quem já existe não é duplicado.`)) return;
    setStarting(true);
    try { const { data } = await api.post(`/integrations/nextfit/units/${unitId}/full-migrate`); setJob(data); }
    catch (e) { toast.error(e.response?.data?.detail || "Não foi possível iniciar"); }
    finally { setStarting(false); }
  };

  return (
    <Card className="p-6 sm:p-10 text-center" data-testid="full-migration-card">
      <div className="mx-auto max-w-xl space-y-5">
        <div className="w-14 h-14 rounded-2xl bg-rose-50 flex items-center justify-center mx-auto"><Rocket className="w-7 h-7 text-rose-600"/></div>
        <div>
          <h2 className="font-heading text-2xl font-black text-slate-900">Migrar {unitName}</h2>
          <p className="text-sm text-slate-600 mt-1">Traz TUDO do Next Fit, sem exceção: todos os alunos (com ou sem CPF, só com nome, homônimos, ativos, inativos e suspensos), os contratos e todas as mensalidades, inclusive as em atraso. Quem já existe com o mesmo cadastro do Next Fit é completado.</p>
        </div>
        {!hasConfig && <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3" data-testid="full-migration-no-key">Cadastre a chave do Next Fit desta unidade em "Ferramentas avançadas" antes de migrar.</div>}
        {!running && (
          <Button size="lg" onClick={start} disabled={!hasConfig || starting} className="w-full sm:w-auto h-auto min-h-14 py-3 px-6 sm:px-10 text-base whitespace-normal bg-rose-600 hover:bg-rose-700 rounded-full" data-testid="full-migration-start-btn">
            {starting ? <Loader2 className="w-5 h-5 mr-2 animate-spin"/> : <Rocket className="w-5 h-5 mr-2"/>}{job?.status === "done" ? "Migrar de novo (só o que falta)" : "Migrar unidade"}
          </Button>
        )}
        {job && (
          <div className="space-y-2 text-left" data-testid="full-migration-progress">
            <div className="flex items-center justify-between text-sm font-semibold">
              <span className="flex items-center gap-2">
                {running && <Loader2 className="w-4 h-4 animate-spin text-rose-600"/>}
                {job.status === "done" && <CheckCircle2 className="w-4 h-4 text-emerald-600"/>}
                {job.status === "failed" && <AlertTriangle className="w-4 h-4 text-rose-600"/>}
                <span data-testid="full-migration-label">{job.status === "done" ? "Migração completa" : job.status === "failed" ? "Migração interrompida" : job.label}</span>
              </span>
              <span className="font-mono" data-testid="full-migration-pct">{job.status === "done" ? 100 : job.pct}%</span>
            </div>
            <Progress value={job.status === "done" ? 100 : job.pct} className="h-3"/>
            {job.status === "failed" && <div className="text-sm text-rose-700" data-testid="full-migration-error">{job.error?.detail}</div>}
          </div>
        )}
      </div>
      {job?.status === "done" && job.result && <div className="mt-6 text-left"><ResultBox r={job.result}/></div>}
    </Card>
  );
};
